/**
 * "+ add phase", and the rename behind a chip's menu (`KLQ-20260827-002`).
 *
 * **One drawer for two acts** because they are the same form: a phase is a definition plus an
 * optional label, and renaming is that form with the definition already decided. Two files
 * would be two copies of the label rule, which is the one thing here with a server-side
 * refusal behind it.
 *
 * THE DUPLICATE CASE IS THE REASON THIS IS A DRAWER AND NOT A MENU.
 *
 * A project may enable the same rung twice — the co-founder's *"pôvodná DSP a nová DSP"*, a
 * building that went back through permitting. The index on `(project_id, definition_id)` is
 * deliberately not unique so that it can. But two rows resolving to the same words is a
 * register nobody can read, so the server answers `422` to a **second** instance with no
 * `label_override`, and this drawer asks for one before it ever gets there: the picker's
 * second group is *"another instance of…"*, and choosing from it makes the label field
 * required. A `<select>` alone could not have asked the follow-up question.
 *
 * The picker offers only the **pinned edition's** rungs, because that is all the server will
 * accept: a definition from another edition is a `422` naming the mismatch (the cross-table
 * check of retro rule 9). It never offers an archived instance for a new one — archiving means
 * *not offered for new work* — but it does count one when deciding whether a pick is a
 * duplicate, because the row still exists and the server still counts it.
 */

import { useMemo, useState } from 'react';

import { Button, Drawer, InlineError, Input, Select } from '@/components';
import type { NormPhase } from '@/features/setup';
import { useNormCatalogue } from '@/features/setup';
import type { MessageKeys } from '@/lib/i18n';
import { useMessages } from '@/lib/i18n';

import type { ProjectPhase } from './api';
import { findNormEdition, phaseLabel } from './phase';
import { phaseErrorKey } from './problems';
import styles from './ProjectsPage.module.css';
import { useCreateProjectPhase, useUpdateProjectPhase } from './queries';

type ProjectsKey = MessageKeys['projects'];

/** `ProjectPhaseCreateRequest.label_override`'s bound. TWIN: `projects/schemas.py`. */
const LABEL_MAX = 120;

export interface PhaseDrawerProps {
  readonly projectId: string;
  /** The edition the project is pinned to. Its rungs are the only ones the server accepts. */
  readonly normVersionId: string | null;
  /** Every phase the project already has, archived included — the duplicate test reads it. */
  readonly phases: readonly ProjectPhase[];
  /** Set to rename that phase instead of adding one. */
  readonly renaming?: ProjectPhase | undefined;
  readonly onClose: () => void;
}

export function PhaseDrawer({
  projectId,
  normVersionId,
  phases,
  renaming,
  onClose,
}: PhaseDrawerProps) {
  const t = useMessages('projects');
  const catalogue = useNormCatalogue();
  const create = useCreateProjectPhase(projectId);
  const update = useUpdateProjectPhase(projectId);

  const edition = findNormEdition(catalogue.data, normVersionId);
  const ladder = useMemo(() => edition?.version.phases ?? [], [edition]);

  /** Which definitions the project already spends, archived rows included — see the header. */
  const enabled = useMemo(
    () => new Set(phases.map((phase) => phase.definition_id)),
    [phases],
  );

  const fresh = useMemo(
    () => ladder.filter((rung) => !enabled.has(rung.id)),
    [ladder, enabled],
  );
  const again = useMemo(
    () => ladder.filter((rung) => enabled.has(rung.id)),
    [ladder, enabled],
  );

  const [definitionId, setDefinitionId] = useState<string>(() => fresh[0]?.id ?? '');
  const [label, setLabel] = useState<string>(() => renaming?.label_override ?? '');
  const [error, setError] = useState<ProjectsKey | null>(null);

  const busy = create.isPending || update.isPending;
  /** A second instance of a rung the project already spends. The server refuses it unlabelled. */
  const duplicate = renaming === undefined && definitionId !== '' && enabled.has(definitionId);

  function rungLabel(rung: NormPhase): string {
    return phaseLabel({ label: rung.name, legacy_name: rung.legacy_name ?? null });
  }

  async function submit(): Promise<void> {
    if (busy) return;
    const trimmed = label.trim();
    setError(null);

    try {
      if (renaming !== undefined) {
        await update.mutateAsync({
          phaseId: renaming.id,
          // Empty clears the override and hands the rung back to the norm's own name — which
          // is why this is `null` rather than an absent key: absent means "leave it alone".
          body: { label_override: trimmed === '' ? null : trimmed },
        });
      } else {
        if (definitionId === '') {
          setError('phase.error.pickOne');
          return;
        }
        if (duplicate && trimmed === '') {
          setError('phase.error.labelRequired');
          return;
        }
        await create.mutateAsync({
          definition_id: definitionId,
          ...(trimmed === '' ? {} : { label_override: trimmed }),
        });
      }
      onClose();
    } catch (failure) {
      setError(phaseErrorKey(failure));
    }
  }

  const nothingLeft = renaming === undefined && ladder.length === 0;

  return (
    <Drawer
      title={renaming === undefined ? t('phase.add.title') : t('phase.rename.title')}
      closeLabel={t('drawer.action.close')}
      onClose={onClose}
      footer={
        <>
          <Button variant="tertiary" disabled={busy} onClick={onClose}>
            {t('drawer.action.cancel')}
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={nothingLeft}
            onClick={() => {
              void submit();
            }}
          >
            {renaming === undefined ? t('phase.add.submit') : t('phase.rename.submit')}
          </Button>
        </>
      }
    >
      <p className={styles.drawerSubtitle}>
        {renaming === undefined ? t('phase.add.subtitle') : t('phase.rename.subtitle')}
      </p>

      {error === null ? null : (
        <div className={styles.drawerError}>
          <InlineError variant="block">{t(error)}</InlineError>
        </div>
      )}

      <form
        className={styles.drawerFields}
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {renaming === undefined ? (
          <div className={styles.drawerField}>
            <Select
              label={t('phase.field.definition.label')}
              value={definitionId}
              disabled={busy || nothingLeft}
              onChange={(event) => {
                setDefinitionId(event.target.value);
                setError(null);
              }}
            >
              {/* Two groups, one control. The second is the duplicate case, and picking from
                  it is what makes the label below required. */}
              {fresh.length === 0 ? null : (
                <optgroup label={t('phase.group.available')}>
                  {fresh.map((rung) => (
                    <option key={rung.id} value={rung.id}>
                      {rungLabel(rung)}
                    </option>
                  ))}
                </optgroup>
              )}
              {again.length === 0 ? null : (
                <optgroup label={t('phase.group.another')}>
                  {again.map((rung) => (
                    <option key={rung.id} value={rung.id}>
                      {rungLabel(rung)}
                    </option>
                  ))}
                </optgroup>
              )}
            </Select>
            <span className={styles.drawerFieldHint}>
              {nothingLeft ? t('phase.hint.noLadder') : t('phase.field.definition.hint')}
            </span>
          </div>
        ) : null}

        <Input
          label={t('phase.field.label.label')}
          placeholder={
            renaming === undefined
              ? t('phase.field.label.placeholder')
              : (renaming.label_override ?? renaming.label)
          }
          hint={duplicate ? t('phase.hint.labelRequired') : t('phase.field.label.hint')}
          maxLength={LABEL_MAX}
          value={label}
          disabled={busy || nothingLeft}
          onChange={(event) => {
            setLabel(event.target.value);
            setError(null);
          }}
        />

        {/* The panel's own submit, so Enter in any field commits. The visible button is in
            the drawer footer, outside this form. */}
        <button type="submit" className="sr-only" disabled={busy}>
          {renaming === undefined ? t('phase.add.submit') : t('phase.rename.submit')}
        </button>
      </form>
    </Drawer>
  );
}
